import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import toast from 'react-hot-toast';
import { Box } from '@mui/material';
import Spinner from '@client/components/Spinner';
import { PATHS } from '@client/routes';
import { EmptyReportedContent } from './EmptyReportedContent';
import { ContentOverview, ReportedItem } from './ContentOverview';
import { ActionModal } from './ActionModal';
import { ContentFilters } from './ReportedContentFilters';
import { useReportedContent } from '../hooks/useReportedContent';
import { useGetReportedContentCountQuery } from 'generated/graphql';
import { communityQueryKeys } from '../constants';
import { WithRouterProps, withRouter } from '@client/router/compat';

type ReportedContentProps = {
  communityId: number;
  influencerId: number;
} & WithRouterProps;

type HandledItem = {
  activityType: string;
  activityId: string;
};

const ReportedContent = ({
  influencerId,
  communityId,
  router,
}: ReportedContentProps) => {
  const queryClient = useQueryClient();
  const {
    reportedContent,
    refetch,
    loading,
    keepContentState,
    isDeleteProgress,
    isKeepProgress,
    hasReportedContent,
    keepContentMutation,
    deletePostMutation,
    deleteReplyMutation,
    blockUserMutation,
    deletePostState,
    deleteReplyState,
    blockUserState,
    initialData,
    setReportedContent,
  } = useReportedContent(communityId);
  const [handledItem, setHandledItem] = useState<null | HandledItem>(null);
  const { refetch: refetchCount } = useGetReportedContentCountQuery({
    variables: { communityId },
  });

  useEffect(() => {
    refetchCount();
  }, []);

  useEffect(() => {
    const keepContent =
      keepContentState.type === 'fetched' &&
      keepContentState?.value?.keepContent;

    const deletePost =
      deletePostState.type === 'fetched' && deletePostState?.value.deletePost;

    const deleteReply =
      deleteReplyState.type === 'fetched' &&
      deleteReplyState?.value.deleteReply;

    if (keepContent || deletePost || deleteReply) {
      refetch();
      refetchCount();
    }

    if (blockUserState.type === 'fetched' && blockUserState?.value) {
      toast.success('User blocked successfully');
      queryClient.refetchQueries({
        queryKey: communityQueryKeys.posts(+communityId),
      });
    }

    if (deletePostState.type === 'fetched' && !deletePostState?.value) {
      toast.error('Something went wrong with deleting post');
    }

    if (deleteReplyState.type === 'fetched' && !deleteReplyState?.value) {
      toast.error('Something went wrong with deleting comment');
    }

    if (keepContentState.type === 'fetched' && !keepContentState?.value) {
      toast.error('Something went wrong with keeping content');
    }

    if (blockUserState.type === 'fetched' && !blockUserState?.value) {
      toast.error('Something went wrong with blocking user');
    }
  }, [
    deletePostState.type,
    deleteReplyState.type,
    blockUserState.type,
    handledItem?.activityType,
    keepContentState,
    refetch,
    refetchCount,
  ]);

  const onConfirmDelete = (item: {
    activityId: string;
    activityType: string;
  }) => {
    setHandledItem(item);
  };

  const onBlock = async (communityId: number, userId: number) => {
    blockUserMutation({
      variables: {
        communityId,
        userId,
      },
    });
  };

  const onDelete = (handledItem: HandledItem) => {
    if (handledItem.activityType === 'post') {
      deletePostMutation({
        variables: {
          postId: handledItem.activityId,
          communityId,
        },
      });
      setHandledItem(null);
      return;
    }
    deleteReplyMutation({
      variables: {
        replyId: handledItem.activityId,
        communityId,
      },
    });
    setHandledItem(null);
  };

  const onKeep = (id: number) => {
    keepContentMutation({
      variables: {
        contentId: id,
      },
    });
  };

  const onDismiss = () => {
    setHandledItem(null);
  };

  const onNavigate = ({
    activityId,
    activityType,
    parentId,
    influencerId,
  }: {
    influencerId: number;
    activityId: string;
    activityType: string;
    parentId?: string;
  }) => {
    if (activityType === 'post') {
      router.push({
        pathname: PATHS.COMMUNITY_POST(influencerId, communityId, activityId),
      });
      return;
    }

    router.push({
      pathname: PATHS.COMMUNITY_POST(influencerId, communityId, parentId),
      hash: `#${activityId}`,
    });
  };

  return (
    <>
      <Box className="pb-5">
        <ContentFilters
          setReportedContent={setReportedContent}
          initialData={initialData}
        />
      </Box>
      {loading ? (
        <Box className="w-full flex justify-center items-center">
          <Spinner />
        </Box>
      ) : hasReportedContent ? (
        <Box className="flex flex-col gap-5 w-[690px]">
          {(reportedContent || []).map((item) => (
            <ContentOverview
              key={item.id}
              item={item as ReportedItem}
              onDelete={() =>
                onConfirmDelete({
                  activityId: item.activityId,
                  activityType: item.activityType,
                })
              }
              onBlock={() => onBlock(communityId, item.author.id)}
              onKeep={() => onKeep(item.id)}
              onNavigate={() =>
                onNavigate({
                  influencerId,
                  activityId: item.activityId,
                  activityType: item.activityType,
                  parentId: item.parentId,
                })
              }
              isKeepProgress={isKeepProgress}
            />
          ))}
        </Box>
      ) : (
        <EmptyReportedContent />
      )}
      <ActionModal
        title={`Are you sure you want to delete this ${handledItem?.activityType}?`}
        onDismiss={onDismiss}
        onSubmit={() => handledItem && onDelete(handledItem)}
        text="This means that it will no longer be visible in your community. All media, reactions and comments within this post will be lost."
        actionText="Delete"
        isModalVisible={handledItem !== null}
        isSubmitting={isDeleteProgress}
      />
    </>
  );
};

export default withRouter(ReportedContent);
